import type {
  OrderOutput,
  OrderStatus,
  OrderPaymentStatus,
  OrganizationsOutput,
  TagsOutput,
} from "@/interfaces";
import type {
  ProductionReport,
  ProductionReportItem,
} from "@/interfaces/production-report";
import type {
  ReceiptReport,
  ReceiptReportItem,
} from "@/interfaces/receipt-report";
import {
  orderStatusTranslation,
  orderPaymentStatusTranslation,
} from "@/interfaces";
import {
  formatCep,
  formatDddPhone,
  formatDocument,
} from "@/util/formatters";

export function orderToProductionReport(
  order: OrderOutput,
  organization?: OrganizationsOutput
): ProductionReport {
  const items: ProductionReportItem[] = order.products.map((product) => ({
    id: product.productId,
    name: product.name,
    quantity: product.quantity,
    unitPrice: product.unitPrice,
    observations: product.observation || "",
    additionals:
      product.additionals?.map((additional) => ({
        itemId: additional.itemId,
        label: additional.label,
        unitPrice: additional.unitPrice,
        unitCost: additional.unitCost,
        quantity: additional.quantity,
        consumptionFactor: additional.consumptionFactor,
      })) || [],
  }));

  // Handle customer information, considering it might not exist
  // Explicitly type the variable to allow for all possible properties
  let customerAttachment: {
    id?: string;
    name: string;
    phone?: string;
    email?: string;
    document?: string;
  } = {
    name: "Cliente não identificado",
  };

  if (order.customer) {
    if (typeof order.customer === "string") {
      customerAttachment = {
        name: order.customer || "Cliente não identificado",
      };
    } else {
      const formattedPhone = order.customer.phoneNumber
        ? formatDddPhone(
            order.customer.ddd,
            order.customer.phoneNumber,
            order.customer.internationalCode
          )
        : "";

      customerAttachment = {
        id: order.customer.id,
        name: order.customer.name || "Cliente não identificado",
        phone: formattedPhone || undefined,
      };
    }
  }

  // Prepare company info if organization is provided
  const companyInfo = organization
    ? {
        name: organization.name,
        document: formatDocument(organization.document) || undefined,
        address: organization.address
          ? `${organization.address.line1}, ${organization.address.number}${
              organization.address.line2
                ? ` - ${organization.address.line2}`
                : ""
            } - ${organization.address.neighborhood}, ${
              organization.address.city
            }${
              formatCep(organization.address.zipCode)
                ? ` - CEP: ${formatCep(organization.address.zipCode)}`
                : ""
            }`
          : undefined,
        phone: organization.phoneNumber
          ? formatDddPhone(
              organization.ddd,
              organization.phoneNumber,
              organization.internationalCode
            ) || undefined
          : undefined,
        logo: organization.file?.url,
      }
    : undefined;

  return {
    id: order.id,
    orderNumber: order.id.substring(0, 8),
    customer: customerAttachment,
    items,
    tags: (order.tags as TagsOutput[]) || [],
    status: orderStatusTranslation[order.status as OrderStatus] || order.status,
    createdAt: new Date(order.orderDate),
    preparationDate: order.preparationDate
      ? new Date(order.preparationDate)
      : undefined,
    delivery: {
      type: order.delivery.deliveryType,
      address: order.delivery.address,
      scheduledFor: order.delivery.deliveryAt
        ? new Date(order.delivery.deliveryAt)
        : undefined,
      deliveryValue: order.delivery.deliveryValue,
    },
    totalAmount: order.totalAmount,
    discount: order.discount || 0,
    additional: order.additional || 0,
    tax: order.tax || 0,
    description: order.description,
    paymentStatus:
      orderPaymentStatusTranslation[
        order.paymentStatus as OrderPaymentStatus
      ] || order.paymentStatus,
    sourceType: "ORDER",
    companyInfo: companyInfo,
  };
}

/**
 * Converte um pedido para o formato do relatório de comprovante
 */
export function orderToReceiptReport(
  order: OrderOutput,
  organization?: OrganizationsOutput
): ReceiptReport {
  const items: ReceiptReportItem[] = order.products.map((product) => ({
    id: product.productId,
    name: product.name,
    quantity: product.quantity,
    unitPrice: product.unitPrice,
    totalPrice: product.quantity * product.unitPrice,
    observations: product.observation || "",
    additionals:
      product.additionals?.map((additional) => ({
        itemId: additional.itemId,
        label: additional.label,
        unitPrice: additional.unitPrice,
        unitCost: additional.unitCost,
        quantity: additional.quantity,
        consumptionFactor: additional.consumptionFactor,
      })) || [],
  }));

  // Calcular o subtotal (soma dos produtos sem descontos/adicionais)
  const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);

  // Handle customer information, considering it might not exist
  // Explicitly type the variable to allow for all possible properties
  let customerAttachment: {
    id?: string;
    name: string;
    phone?: string;
    email?: string;
    document?: string;
  } = {
    name: "Cliente não identificado",
  };

  if (order.customer) {
    if (typeof order.customer === "string") {
      customerAttachment = {
        name: order.customer || "Cliente não identificado",
      };
    } else {
      const formattedPhone = order.customer.phoneNumber
        ? formatDddPhone(
            order.customer.ddd,
            order.customer.phoneNumber,
            order.customer.internationalCode
          )
        : "";

      customerAttachment = {
        id: order.customer.id,
        name: order.customer.name || "Cliente não identificado",
        phone: formattedPhone || undefined,
        document: formatDocument(order.customer.document) || undefined,
      };
    }
  }

  // Prepare company info if organization is provided
  const companyInfo = organization
    ? {
        name: organization.name,
        document: formatDocument(organization.document) || undefined,
        address: organization.address
          ? `${organization.address.line1}, ${organization.address.number}${
              organization.address.line2
                ? ` - ${organization.address.line2}`
                : ""
            } - ${organization.address.neighborhood}, ${
              organization.address.city
            }${
              formatCep(organization.address.zipCode)
                ? ` - CEP: ${formatCep(organization.address.zipCode)}`
                : ""
            }`
          : undefined,
        phone: organization.phoneNumber
          ? formatDddPhone(
              organization.ddd,
              organization.phoneNumber,
              organization.internationalCode
            ) || undefined
          : undefined,
        logo: organization.file?.url,
      }
    : undefined;

  return {
    id: order.id,
    orderNumber: order.id.substring(0, 8),
    customer: customerAttachment,
    items,
    tags: (order.tags as TagsOutput[]) || [],
    status: orderStatusTranslation[order.status as OrderStatus] || order.status,
    createdAt: new Date(order.orderDate),
    preparationDate: order.preparationDate
      ? new Date(order.preparationDate)
      : undefined,
    delivery: {
      type: order.delivery.deliveryType,
      address: order.delivery.address,
      scheduledFor: order.delivery.deliveryAt
        ? new Date(order.delivery.deliveryAt)
        : undefined,
      deliveryValue: order.delivery.deliveryValue,
    },
    totalAmount: order.totalAmount,
    subtotal,
    discount: order.discount || 0,
    additional: order.additional || 0,
    tax: order.tax || 0,
    description: order.description,
    payments: order.payments
      ? order.payments.map((payment) => ({
          method: payment.method,
          amount: payment.amount,
          date: new Date(payment.paymentDate),
        }))
      : [],
    paymentStatus:
      orderPaymentStatusTranslation[
        order.paymentStatus as OrderPaymentStatus
      ] || order.paymentStatus,
    companyInfo: companyInfo,
  };
}
